import { Control, useWatch } from 'react-hook-form';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { CircleCheckBig } from 'lucide-react';

import { Card, CardContent } from 'components/Card';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';

import { CarrierCheckInInputs, CheckInName } from './types';
import { buildCheckInSummaryItems, calculateSendTimeForSummary } from './util';

dayjs.extend(timezone);

type EmailsToScheduleSummaryProps = {
  control: Control<CarrierCheckInInputs>;
  load: NormalizedLoad;
  isPickupInPast: boolean;
  isDropoffInPast: boolean;
};

export const EmailsToScheduleSummary = ({
  control,
  load,
  isPickupInPast,
  isDropoffInPast,
}: EmailsToScheduleSummaryProps) => {
  const dispatch = useWatch({ control, name: CheckInName.Dispatch });
  const pickup = useWatch({ control, name: CheckInName.Pickup });
  const afterPickup = useWatch({ control, name: CheckInName.AfterPickup });
  const inTransit = useWatch({ control, name: CheckInName.InTransit });
  const dropoff = useWatch({ control, name: CheckInName.Dropoff });
  const afterDropoff = useWatch({ control, name: CheckInName.AfterDropoff });

  const checkIns = buildCheckInSummaryItems(
    dispatch,
    pickup,
    afterPickup,
    inTransit,
    dropoff,
    afterDropoff,
    load,
    isPickupInPast,
    isDropoffInPast
  );

  const scheduledEmails = checkIns
    .map((checkIn) => ({
      label: checkIn.label,
      sendTime: calculateSendTimeForSummary(checkIn, load),
    }))
    .filter((item) => item.sendTime !== null);

  if (scheduledEmails.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className='p-2.5'>
        <Flex direction='col' gap='sm'>
          <Typography
            variant='body-sm'
            weight='semibold'
            className='text-neutral-900'
          >
            Emails to be scheduled
          </Typography>
          <Flex direction='col' gap='xs'>
            {scheduledEmails.map((email, index) => (
              <Flex key={index} align='center' gap='sm' className='w-full'>
                <CircleCheckBig className='h-4 w-4 text-success-600 shrink-0' />
                <Flex direction='col' gap='none' className='flex-1 min-w-0'>
                  <Typography
                    variant='body-sm'
                    weight='medium'
                    className='text-neutral-900'
                  >
                    {email.label}
                  </Typography>
                  <Typography variant='body-xs' className='text-neutral-600'>
                    {email.sendTime}
                  </Typography>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};
